import { ACTION_ERROR_CODES } from "./contract";
import { CONFLICT_ERROR_CODE, RETRYABLE_FAILURES, SLOT_FIELD } from "./constants";
import type { ActionLabels } from "./labels";

/** What a card needs to know about a failed slots / confirm call (never raw server text). */
export interface ActionFailure {
  code: string;
  /** Seconds from `Retry-After`. */
  retryAfter: number | undefined;
  /** Form fields the server named in a `validation_error` (`details.fields`, comma separated). */
  fields: string[];
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

function namedFields(error: Record<string, unknown>): string[] {
  const names = new Set<string>();
  const details = error.details;
  const listed = isRecord(details) ? details.fields : undefined;
  if (typeof listed === "string") {
    for (const name of listed.split(",")) if (name.trim()) names.add(name.trim());
  }
  if (isRecord(error.fields)) for (const name of Object.keys(error.fields)) names.add(name);
  return [...names];
}

/**
 * Reads the shape of the API client's `ApiError` (`code`, `retryAfter`, `details.fields`) without
 * importing it: the package stays free of the client, and hosts may throw any object with these keys.
 */
export function toActionFailure(error: unknown): ActionFailure {
  if (!isRecord(error)) return { code: "unknown", retryAfter: undefined, fields: [] };
  const code = typeof error.code === "string" && error.code ? error.code : "unknown";
  const retryAfter = typeof error.retryAfter === "number" && Number.isFinite(error.retryAfter) ? error.retryAfter : undefined;
  return { code, retryAfter, fields: namedFields(error) };
}

export interface FailureView {
  message: string;
  /** Offer a "Try again" button next to the message. */
  retryable: boolean;
}

/** Visitor-facing copy for a failure code (cards handle `action_expired` / `slot_unavailable` themselves). */
export function describeFailure(failure: ActionFailure, labels: ActionLabels): FailureView {
  return { message: failureMessage(failure, labels), retryable: RETRYABLE_FAILURES.has(failure.code) };
}

function failureMessage({ code, retryAfter, fields }: ActionFailure, labels: ActionLabels): string {
  switch (code) {
    case ACTION_ERROR_CODES.slotUnavailable:
      return labels.slotTaken;
    case ACTION_ERROR_CODES.validation:
      return fields.includes(SLOT_FIELD) ? labels.pickATime : labels.validation;
    case ACTION_ERROR_CODES.rateLimited:
      return labels.rateLimited(retryAfter);
    case ACTION_ERROR_CODES.unavailable:
      return labels.serviceUnavailable;
    case ACTION_ERROR_CODES.captchaRequired:
    case ACTION_ERROR_CODES.captchaFailed:
      return labels.captcha;
    case ACTION_ERROR_CODES.authRequired:
      return labels.signIn;
    case CONFLICT_ERROR_CODE:
      return labels.conflict;
    default:
      return labels.generic;
  }
}
